import { apiClient } from './apiClient';

/**
 * QR Public Service
 * Handles fetching public QR code and card data using session-based access
 *
 * Session Flow:
 * 1. POST /qrcodes/:qrCodeId/session - Creates a scan session
 * 2. GET /qrcodes/session/:sessionToken - Retrieves card data for the session
 *
 * Sessions have a limited duration (scanDurationSeconds) after which they expire.
 * Users must rescan the QR code to create a new session.
 */
class QrPublicService {
  /**
   * Start a new scan session for a QR code
   * This creates a time-limited access window for viewing the card data
   *
   * @param {string} qrCodeId - The QR code ID from the URL
   * @returns {Promise<{sessionToken: string, expiresAt: string}>} Session token and expiration
   * @throws Error with message: QR_NOT_FOUND, QR_DISABLED, QR_EXPIRED, QR_NO_CARD
   */
  async startScanSession(qrCodeId) {
    try {
      console.log(`🔐 [QR Session] Starting scan session for QR: ${qrCodeId}`);

      const data = await apiClient.post(`/qrcodes/${qrCodeId}/session`, {});

      console.log('✅ [QR Session] Session created:', {
        sessionToken: data.sessionToken ? `${data.sessionToken.substring(0, 8)}...` : 'N/A',
        expiresAt: data.expiresAt,
      });

      return data;
    } catch (error) {
      console.error('❌ [QR Session] Failed to start session:', error.message);
      throw error;
    }
  }

  /**
   * Get session data using an active session token
   * Returns the card information associated with the session
   *
   * @param {string} sessionToken - The session token from startScanSession
   * @returns {Promise<Object>} Session data including qrCodeId, cardId, allergies, sessionExpiresAt
   * @throws Error with message: SESSION_EXPIRED, SESSION_INVALID
   */
  async getSessionData(sessionToken) {
    try {
      console.log(`📋 [QR Session] Fetching session data...`);

      const data = await apiClient.get(`/qrcodes/session/${sessionToken}`);

      console.log('✅ [QR Session] Session data retrieved:', {
        qrCodeId: data.qrCodeId,
        cardId: data.cardId,
        cardName: data.cardName,
        allergiesCount: data.allergies?.length || 0,
        sessionExpiresAt: data.sessionExpiresAt,
      });

      return data;
    } catch (error) {
      console.error('❌ [QR Session] Failed to get session data:', error.message);
      throw error;
    }
  }

  /**
   * Get public QR code information using session-based access
   * This is the main method to call when loading a QR code page
   *
   * Flow:
   * 1. Creates a new scan session (POST /qrcodes/:qrCodeId/session)
   * 2. Retrieves session data (GET /qrcodes/session/:sessionToken)
   * 3. Returns the combined data for rendering
   *
   * @param {string} qrCodeId - The QR code ID from the URL or scanned code
   * @returns {Promise<Object>} Session data including card information and allergies
   * @throws Error with message: QR_NOT_FOUND, QR_DISABLED, QR_EXPIRED, QR_NO_CARD,
   *         SESSION_EXPIRED, SESSION_INVALID
   */
  async getPublicQrCode(qrCodeId) {
    try {
      console.log(`📱 [QR Public Service] Fetching QR code with session: ${qrCodeId}`);

      // Step 1: Create a scan session
      const sessionResponse = await this.startScanSession(qrCodeId);
      const { sessionToken } = sessionResponse;

      if (!sessionToken) {
        throw new Error('SESSION_INVALID');
      }

      // Step 2: Get session data (card info, allergies, etc.)
      const sessionData = await this.getSessionData(sessionToken);

      console.log('✅ [QR Public Service] QR code data retrieved via session:', {
        qrCodeId: sessionData.qrCodeId,
        cardId: sessionData.cardId,
        cardName: sessionData.cardName,
        disabled: sessionData.disabled,
        expired: sessionData.expired,
        allergiesCount: sessionData.allergies?.length || 0,
        sessionExpiresAt: sessionData.sessionExpiresAt,
      });

      return sessionData;
    } catch (error) {
      console.error('❌ [QR Public Service] Error fetching QR code:', error.message);
      throw error;
    }
  }

  /**
   * Get card details (if more detailed card info is needed)
   * Note: This may require authentication depending on your API setup
   * For public QR pages, getPublicQrCode() should provide all necessary data
   *
   * @param {string} cardId - The card ID
   * @returns {Promise} Card data
   */
  async getCard(cardId) {
    try {
      console.log(`🃏 [QR Public Service] Fetching card: ${cardId}`);

      const data = await apiClient.get(`/cards/${cardId}`);

      console.log('✅ [QR Public Service] Card data retrieved:', {
        cardId: data.id,
        cardName: data.cardName,
        allergiesCount: data.allergies?.length || 0,
      });

      return data;
    } catch (error) {
      console.error('❌ [QR Public Service] Error fetching card:', error);
      throw error;
    }
  }

  /**
   * Get QR code image URL
   *
   * @param {string} uid - User ID
   * @param {string} qrCodeId - QR code ID
   * @returns {string} Full URL to the QR code image
   */
  getQrCodeImageUrl(uid, qrCodeId) {
    return apiClient.getQrCodeImageUrl(uid, qrCodeId);
  }

  /**
   * Get card logo URL
   *
   * @param {string} uid - User ID
   * @param {string} cardId - Card ID
   * @returns {string} Full URL to the card logo image
   */
  getCardLogoUrl(uid, cardId) {
    return apiClient.getCardLogoUrl(uid, cardId);
  }

  /**
   * Check if the backend API is available
   * Useful for debugging connection issues
   *
   * @returns {Promise<boolean>} true if backend is reachable, false otherwise
   */
  async checkApiHealth() {
    return await apiClient.healthCheck();
  }
}

// Export singleton instance
export const qrPublicService = new QrPublicService();
